import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, dashboardHomeFor } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [ref, setRef] = useState({ academic_levels: [], domains: [] });
  const [form, setForm] = useState({
    academic_level: "graduate", domains: [], student_type: "undergraduate", affiliation: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login");
    api.get("/auth/reference").then((r) => {
      setRef({ academic_levels: r.data.academic_levels, domains: r.data.domains });
    });
    if (user?.affiliation) setForm((f) => ({ ...f, affiliation: user.affiliation }));
  }, [user, navigate]);

  const toggleDomain = (d) => {
    setForm((f) => ({
      ...f,
      domains: f.domains.includes(d) ? f.domains.filter((x) => x !== d) : [...f.domains, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.domains.length === 0) { toast.error("Pick at least one research interest"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/complete-profile", form);
      setUser(r.data);
      toast.success("Profile complete — welcome to KhojByte!");
      navigate(dashboardHomeFor(r.data.role));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="onboarding-profile-page">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">STEP 3 OF 3</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-1 leading-tight">
          Tell us about your <em className="italic text-kb-primary">research interests</em>.
        </h1>
        <p className="mt-3 text-kb-ink-muted">We'll personalise mentor matches, journal recommendations and learning paths to fit your level and domains.</p>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="onboarding-profile-form">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">ACADEMIC LEVEL</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ref.academic_levels.map((l) => (
                <button type="button" key={l}
                  onClick={() => setForm((f) => ({
                    ...f, academic_level: l,
                    student_type: l === "undergraduate" ? "undergraduate" : "graduate",
                  }))}
                  className={`px-3 py-2.5 rounded-sm border text-sm capitalize transition-colors ${
                    form.academic_level === l ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 hover:border-kb-primary"
                  }`}
                  data-testid={`academic-${l}`}>{l}</button>
              ))}
            </div>
          </div>

          {user?.role === "student" && (
            <div>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">STUDENT TYPE</div>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {["undergraduate", "graduate"].map((s) => (
                  <button type="button" key={s}
                    onClick={() => setForm((f) => ({ ...f, student_type: s }))}
                    className={`px-3 py-2.5 rounded-sm border text-sm capitalize ${
                      form.student_type === s ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200"
                    }`}
                    data-testid={`student-type-${s}`}>
                    {s} {s === "undergraduate" ? "(Project workflow)" : "(Thesis workflow)"}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">RESEARCH DOMAINS · pick all that apply</div>
            <div className="flex flex-wrap gap-2">
              {ref.domains.map((d) => {
                const active = form.domains.includes(d);
                return (
                  <button type="button" key={d} onClick={() => toggleDomain(d)}
                    className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border transition-colors ${
                      active ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink hover:border-kb-primary"
                    }`}
                    data-testid={`domain-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    {d}
                  </button>
                );
              })}
            </div>
            <div className="kb-mono text-[10px] text-kb-ink-light mt-2">{form.domains.length} selected</div>
          </div>

          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">AFFILIATION (optional)</div>
            <input className="kb-input max-w-md" placeholder="e.g. Tribhuvan University"
              value={form.affiliation} onChange={(e) => setForm({ ...form, affiliation: e.target.value })}
              data-testid="onboarding-affiliation" />
          </div>

          <button type="submit" disabled={submitting} className="kb-btn-primary" data-testid="onboarding-profile-submit">
            {submitting ? "Saving…" : (<>Finish & enter workspace <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
